import { LitElement, html, css } from "lit";
import { customElement, state, query } from "lit/decorators.js";

import "@material/web/progress/circular-progress.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/text-button.js";
import "@material/web/textfield/outlined-text-field.js";
import "@material/web/select/outlined-select.js";
import "@material/web/select/select-option.js";
import "@material/web/dialog/dialog.js";
import "@material/web/icon/icon.js";
import "@material/web/chips/chip-set.js";
import "@material/web/chips/filter-chip.js";
import "@material/web/menu/menu.js";
import "@material/web/menu/menu-item.js";
import type { MdDialog } from "@material/web/dialog/dialog.js";

import "../components/app-shell";
import { api } from "../lib/api";
import { sharedStyles } from "../styles/shared";
import { inputValue, errorMessage } from "../lib/dom";
import { icon } from "../lib/icons";
import { toast } from "../lib/toast";
import { invalidateNavCounts } from "../lib/nav-counts";
import { AuthController } from "../lib/auth-controller";
import type { Product, NfcTag, NfcBatch, NfcTerminalState, TapReport } from "../lib/types";

// Human labels for a tag's terminal provisioning state (arch doc §4).
const TERMINAL_LABELS: Record<NfcTerminalState, string> = {
  written_ok: "Written",
  write_failed: "Write failed",
  destroyed_at_write: "Destroyed (write)",
  destroyed_at_packaging: "Destroyed (packaging)",
};

type TagFilter = "all" | "flagged" | "wait";

// The "NFC tags" console: production batches and the NTAG 424 DNA tags bound to
// them. Authoring (create batch, provision tag) is role-gated to editor/admin —
// the server enforces it too; we just hide the controls from viewers.
@customElement("nfc-page")
export class NfcPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .expiry-warn {
        padding: 4px;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .expiry-row {
        border-radius: 10px;
        padding: 12px 14px;
        background: var(--md-sys-color-surface-variant);
        color: var(--md-sys-color-on-surface-variant);
      }
      .expiry-row--bad {
        background: var(--md-sys-color-error-container);
        color: var(--md-sys-color-on-error-container);
      }
      .expiry-row b {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 0.875rem;
      }
      .expiry-row p {
        margin: 4px 0 0;
        font-size: 0.8125rem;
        line-height: 1.45;
        max-width: 78ch;
      }
      form.dialog-form {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding-top: 8px;
        min-width: min(420px, 70vw);
      }

      /* Provisioned-vs-authorized progress shown on each batch row. */
      .progress {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 160px;
      }

      .report-note {
        min-width: 220px;
        max-width: 360px;
        overflow-wrap: anywhere;
        font-size: 13px;
      }

      .progress__track {
        flex: 1;
        height: 6px;
        border-radius: 999px;
        background: var(--md-sys-color-surface-container-highest);
        overflow: hidden;
        display: flex;
      }

      .progress__seg {
        height: 100%;
      }

      .seg--ok {
        background: var(--md-sys-color-primary);
      }
      .seg--failed {
        background: #f59e0b;
      }
      .seg--destroyed {
        background: var(--md-sys-color-error);
      }

      .progress__label {
        font-size: 0.75rem;
        color: var(--md-sys-color-on-surface-variant);
        white-space: nowrap;
      }

      .tag-actions {
        text-align: right;
        white-space: nowrap;
      }

      /* M3 split button: connected tonal segments, full-pill shape. */
      .preview-split {
        display: inline-flex;
        align-items: stretch;
        border-radius: var(--md-sys-shape-corner-full);
        overflow: hidden;
        gap: 2px;
        background: var(--md-sys-color-surface-container-high);
        transition: border-radius var(--md-sys-motion-duration-medium)
          var(--md-sys-motion-spring-spatial);
      }
      /* Shape-morph: the pill relaxes to a large-radius shape while pressed. */
      .preview-split:has(.preview-btn:active),
      .preview-split:has(.preview-caret:active) {
        border-radius: var(--md-sys-shape-corner-large);
      }

      .preview-btn,
      .preview-caret {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        border: none;
        background: var(--md-sys-color-secondary-container);
        color: var(--md-sys-color-on-secondary-container);
        font: 500 14px/20px var(--font-plain, sans-serif);
        letter-spacing: 0.006em;
        cursor: pointer;
        transition:
          background-color var(--md-sys-motion-duration-short)
            var(--md-sys-motion-emphasized),
          transform var(--md-sys-motion-duration-short)
            var(--md-sys-motion-spring-spatial);
        --md-icon-size: 18px;
      }
      .preview-btn:active,
      .preview-caret:active {
        transform: scale(0.94);
      }

      .preview-btn {
        padding: 0 16px 0 18px;
        height: 40px;
      }

      .preview-caret {
        padding: 0 8px;
        --md-icon-size: 22px;
      }

      .preview-btn:hover,
      .preview-caret:hover {
        background: color-mix(
          in srgb,
          var(--md-sys-color-on-secondary-container) 8%,
          var(--md-sys-color-secondary-container)
        );
      }
      .preview-btn:active,
      .preview-caret:active {
        background: color-mix(
          in srgb,
          var(--md-sys-color-on-secondary-container) 10%,
          var(--md-sys-color-secondary-container)
        );
      }

      .preview-btn:focus-visible,
      .preview-caret:focus-visible {
        outline: 3px solid var(--md-sys-color-secondary);
        outline-offset: -3px;
        z-index: 1;
      }

      .tag-actions {
        position: relative;
      }

      /* ── Live fleet overview — Material 3 Expressive ──
         Tonal surface layering, M3 shape scale, spring motion. */
      .fleet {
        border-radius: var(--md-sys-shape-corner-extra-large);
        padding: var(--space-6) var(--space-6) var(--space-5);
        background: var(--md-sys-color-surface-container);
        --tone: var(--md-sys-color-primary);
        --tone-container: var(--md-sys-color-primary-container);
        --on-tone-container: var(--md-sys-color-on-primary-container);
      }
      .fleet--alert {
        --tone: var(--md-sys-color-error);
        --tone-container: var(--md-sys-color-error-container);
        --on-tone-container: var(--md-sys-color-on-error-container);
      }
      .fleet--pending {
        --tone: var(--md-sys-color-tertiary);
        --tone-container: var(--md-sys-color-tertiary-container);
        --on-tone-container: var(--md-sys-color-on-tertiary-container);
      }
      .fleet--idle {
        --tone: var(--md-sys-color-outline);
        --tone-container: var(--md-sys-color-surface-container-highest);
        --on-tone-container: var(--md-sys-color-on-surface-variant);
      }

      .fleet__head {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .fleet__pulse {
        width: 10px;
        height: 10px;
        border-radius: var(--md-sys-shape-corner-full);
        flex: none;
        background: var(--tone);
        animation: fleetpulse 2.6s var(--md-sys-motion-emphasized) infinite;
      }
      @keyframes fleetpulse {
        0% {
          box-shadow: 0 0 0 0 color-mix(in srgb, var(--tone) 55%, transparent);
        }
        70% {
          box-shadow: 0 0 0 10px transparent;
        }
        100% {
          box-shadow: 0 0 0 0 transparent;
        }
      }

      /* M3 headline-small / body-medium. */
      .fleet__title {
        font: 400 clamp(20px, 2.4vw, 24px) / 1.33 var(--font-brand, sans-serif);
        letter-spacing: 0;
        color: var(--md-sys-color-on-surface);
      }
      .fleet__sub {
        margin-top: 2px;
        font: 400 14px/20px var(--font-plain, sans-serif);
        letter-spacing: 0.016em;
        color: var(--md-sys-color-on-surface-variant);
      }
      .fleet__pct {
        margin-inline-start: auto;
        text-align: end;
        line-height: 1.05;
      }
      .fleet__pct-n {
        display: block;
        font: 400 28px/1 var(--font-brand, sans-serif);
        color: var(--tone);
      }
      .fleet__pct span:last-child {
        font: 500 11px/16px var(--font-plain, sans-serif);
        text-transform: uppercase;
        letter-spacing: 0.09em;
        color: var(--md-sys-color-on-surface-variant);
      }

      /* M3 Expressive wavy determinate indicator: the active portion is a
         flowing sine line, the remaining track a flat line. */
      .fleet__bar {
        position: relative;
        margin: var(--space-4) 0 var(--space-2);
        height: 14px;
      }
      /* Flat remaining-track line, centred. */
      .fleet__bar::before {
        content: "";
        position: absolute;
        inset-inline: 0;
        top: 50%;
        height: 4px;
        transform: translateY(-50%);
        border-radius: var(--md-sys-shape-corner-full);
        background: var(--md-sys-color-surface-container-highest);
      }
      /* Active wavy fill — a tone-coloured band masked to a sine, scrolling. */
      .fleet__fill {
        position: absolute;
        inset-block: 0;
        inset-inline-start: 0;
        width: var(--pct, 0%);
        background: var(--tone);
        -webkit-mask: var(--wave) repeat-x left center / 44px 100%;
        mask: var(--wave) repeat-x left center / 44px 100%;
        --wave: url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='44'%20height='14'%3E%3Cpath%20d='M0%207%20Q11%202.5%2022%207%20T44%207'%20stroke='%23000'%20stroke-width='4'%20fill='none'%20stroke-linecap='round'/%3E%3C/svg%3E");
        animation:
          fleetgrow var(--md-sys-motion-duration-long)
            var(--md-sys-motion-spring-spatial) both,
          fleetwave 1.1s linear infinite;
        transition: width var(--md-sys-motion-duration-long)
          var(--md-sys-motion-spring-spatial);
      }
      @keyframes fleetgrow {
        from {
          width: 0%;
        }
      }
      @keyframes fleetwave {
        to {
          -webkit-mask-position: -44px center;
          mask-position: -44px center;
        }
      }

      .fleet__metrics {
        margin-top: var(--space-4);
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--space-2);
      }
      @media (min-width: 560px) {
        .fleet__metrics {
          grid-template-columns: repeat(4, 1fr);
        }
      }

      /* Metric = M3 tonal container. Inactive: surface-container-high.
         Selected (aria-pressed): filled with its container colour role. */
      .metric {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
        padding: var(--space-4);
        border: none;
        border-radius: var(--md-sys-shape-corner-large);
        background: var(--md-sys-color-surface-container-high);
        color: var(--md-sys-color-on-surface);
        text-align: start;
        cursor: pointer;
        overflow: hidden;
        transition:
          transform var(--md-sys-motion-duration-medium)
            var(--md-sys-motion-spring-spatial),
          border-radius var(--md-sys-motion-duration-medium)
            var(--md-sys-motion-spring-spatial),
          background-color var(--md-sys-motion-duration-short)
            var(--md-sys-motion-emphasized);
        --m: var(--md-sys-color-primary);
        --m-container: var(--md-sys-color-primary-container);
        --on-m-container: var(--md-sys-color-on-primary-container);
      }
      .metric--pending {
        --m: var(--md-sys-color-tertiary);
        --m-container: var(--md-sys-color-tertiary-container);
        --on-m-container: var(--md-sys-color-on-tertiary-container);
      }
      .metric--alert {
        --m: var(--md-sys-color-error);
        --m-container: var(--md-sys-color-error-container);
        --on-m-container: var(--md-sys-color-on-error-container);
      }
      /* M3 state layer on hover/press. */
      .metric::before {
        content: "";
        position: absolute;
        inset: 0;
        background: var(--m);
        opacity: 0;
        transition: opacity var(--md-sys-motion-duration-short)
          var(--md-sys-motion-emphasized);
        pointer-events: none;
      }
      .metric:hover:not([disabled])::before {
        opacity: 0.08;
      }
      .metric:active:not([disabled])::before {
        opacity: 0.1;
      }
      .metric:hover:not([disabled]) {
        transform: translateY(-2px);
        border-radius: var(--md-sys-shape-corner-large-increased);
      }
      .metric:active:not([disabled]) {
        transform: scale(0.96);
        border-radius: var(--md-sys-shape-corner-small);
      }
      /* A selected tile settles into the extra-large expressive shape with an
         M3-Expressive spring overshoot (radius + a subtle scale pop). */
      .metric[aria-pressed="true"] {
        border-radius: var(--md-sys-shape-corner-extra-large);
        animation: metricselect 0.5s var(--md-sys-motion-spring-spatial);
      }
      @keyframes metricselect {
        0% {
          border-radius: var(--md-sys-shape-corner-small);
          transform: scale(0.97);
        }
        55% {
          border-radius: var(--md-sys-shape-corner-extra-large-increased);
          transform: scale(1.04);
        }
        100% {
          border-radius: var(--md-sys-shape-corner-extra-large);
          transform: scale(1);
        }
      }
      .metric:focus-visible {
        outline: 3px solid var(--m);
        outline-offset: 2px;
      }
      .metric[disabled] {
        cursor: default;
        opacity: 0.5;
      }
      .metric[aria-pressed="true"] {
        background: var(--m-container);
        color: var(--on-m-container);
      }
      .metric[aria-pressed="true"] .metric__l {
        color: color-mix(in srgb, var(--on-m-container) 78%, transparent);
      }
      /* M3 display-small number. */
      .metric__n {
        font: 400 clamp(28px, 4vw, 36px) / 1.1 var(--font-brand, sans-serif);
        color: inherit;
        font-variant-numeric: tabular-nums;
        letter-spacing: 0;
      }
      /* M3 label-large. */
      .metric__l {
        font: 500 13px/16px var(--font-plain, sans-serif);
        letter-spacing: 0.006em;
        color: var(--md-sys-color-on-surface-variant);
      }

      @media (prefers-reduced-motion: reduce) {
        .fleet__pulse,
        .fleet__fill,
        .metric[aria-pressed="true"] {
          animation: none;
        }
        .fleet__fill,
        .metric,
        .metric::before,
        .preview-split,
        .preview-btn,
        .preview-caret {
          transition: none;
        }
      }


      /* Reconciliation legend under the bar. */
      .recon {
        display: flex;
        align-items: center;
        gap: 5px;
        flex-wrap: wrap;
        margin-top: 6px;
        font-size: 0.6875rem;
        color: var(--md-sys-color-on-surface-variant);
      }

      .recon__dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        display: inline-block;
        margin-left: 4px;
      }
      .recon__dot--ok {
        background: var(--md-sys-color-primary);
      }
      .recon__dot--failed {
        background: #f59e0b;
      }
      .recon__dot--destroyed {
        background: var(--md-sys-color-error);
      }

      .header-actions {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }
    `,
  ];

  private auth = new AuthController(this);

  @state() private products: Product[] = [];
  @state() private batches: NfcBatch[] = [];
  @state() private tags: NfcTag[] = [];
  @state() private reports: TapReport[] = [];
  @state() private tagFilter: TagFilter = "all";
  @state() private openPreviewMenu = "";
  /** 0→1 count-up progress for the fleet metrics (runs once when data lands). */
  @state() private countProgress = 1;
  private countAnimated = false;
  @state() private loading = true;
  @state() private error = "";

  // Create-batch dialog state.
  @state() private batchProduct = "";
  @state() private batchCount = "";
  @state() private batchManufactured = "";
  @state() private batchExpires = "";
  @state() private creatingBatch = false;
  @state() private batchError = "";

  // Provision-tag dialog state.
  @state() private tagUID = "";
  @state() private tagProduct = "";
  @state() private tagBatch = "";
  @state() private provisioning = false;
  @state() private tagError = "";

  @query("#batch-dialog") private batchDialog?: MdDialog;
  @query("#tag-dialog") private tagDialog?: MdDialog;

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  protected updated(): void {
    // Count the fleet metrics up from zero once, when the tags first load —
    // the "coming alive" moment (skipped for reduced motion).
    if (this.loading || this.tags.length === 0 || this.countAnimated) return;
    this.countAnimated = true;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    this.countProgress = 0;
    const start = performance.now();
    const dur = 750;
    const tick = (now: number): void => {
      const t = Math.min(1, (now - start) / dur);
      this.countProgress = 1 - Math.pow(1 - t, 3); // easeOutCubic
      if (t < 1) requestAnimationFrame(tick);
      else this.countProgress = 1;
    };
    requestAnimationFrame(tick);
  }

  private get canAuthor(): boolean {
    // org_admin, not "admin" — the backend has no "admin" role, and the old
    // test silently hid batch authoring from every org admin.
    return this.auth.hasRole("org_admin") || this.auth.hasRole("editor");
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = "";
    try {
      const [products, batchResp, tagResp, reports] = await Promise.all([
        api.get<Product[]>("/catalog/products"),
        api.get<{ batches: NfcBatch[] | null }>("/nfc/batches"),
        api.get<{ tags: NfcTag[] | null }>("/nfc/tags"),
        api.get<TapReport[] | null>("/nfc/reports"),
      ]);
      this.products = products;
      this.batches = batchResp.batches ?? [];
      this.tags = tagResp.tags ?? [];
      this.reports = reports ?? [];
    } catch (err) {
      this.error = errorMessage(err, "Failed to load NFC data.");
    } finally {
      this.loading = false;
    }
  }

  private productName(id: string): string {
    return this.products.find((p) => p.ID === id)?.Name ?? "—";
  }

  // Tags provisioned against a batch, for the authorized-vs-provisioned gauge.
  private provisionedCount(batchID: string): number {
    return this.tags.filter((t) => t.BatchID === batchID).length;
  }

  // Reconciliation buckets: flagged = failed/destroyed at provisioning;
  // wait = written but never read in the field yet.
  private isFlagged(t: NfcTag): boolean {
    return t.TerminalState !== "written_ok";
  }

  private isWaiting(t: NfcTag): boolean {
    return t.TerminalState === "written_ok" && t.CounterLastSeen === 0;
  }

  private get filteredTags(): NfcTag[] {
    switch (this.tagFilter) {
      case "flagged":
        return this.tags.filter((t) => this.isFlagged(t));
      case "wait":
        return this.tags.filter((t) => this.isWaiting(t));
      default:
        return this.tags;
    }
  }

  private formatDate(value: string): string {
    const d = new Date(value);
    if (isNaN(d.getTime()) || d.getFullYear() < 1970) return "—";
    return d.toLocaleDateString();
  }

  /** Whole days from today (UTC) until a batch's best-before; null when the
   * batch has no expiry. Shared by the warning banner and the table cell so
   * both agree, and matched to the server's UTC-midnight arithmetic. */
  private daysToExpiry(b: NfcBatch): number | null {
    const e = new Date(b.ExpiresAt);
    if (isNaN(e.getTime()) || e.getFullYear() < 1970) return null;
    const now = new Date();
    return Math.round(
      (Date.UTC(e.getUTCFullYear(), e.getUTCMonth(), e.getUTCDate()) -
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())) /
        86400000,
    );
  }

  // Expired stock still carries live tags, so a consumer can tap a unit that is
  // past its date and get a clean "authentic" — worth flagging to the producer.
  private renderExpiryWarning() {
    const expired: NfcBatch[] = [];
    const soon: NfcBatch[] = [];
    for (const b of this.batches) {
      const d = this.daysToExpiry(b);
      if (d === null) continue;
      if (d < 0) expired.push(b);
      else if (d <= 30) soon.push(b);
    }
    if (expired.length === 0 && soon.length === 0) return null;

    const names = (list: NfcBatch[]) =>
      [...new Set(list.map((b) => this.productName(b.ProductID)))].join(", ");
    return html`
      <div class="card card--flush expiry-warn">
        ${expired.length > 0
          ? html`<div class="expiry-row expiry-row--bad">
              <b>${icon("flag")} ${expired.length}
                ${expired.length === 1 ? "batch has" : "batches have"} passed
                their best-before date</b>
              <p>
                ${names(expired)} — tags in
                ${expired.length === 1 ? "this batch" : "these batches"} still
                verify as authentic, so any remaining stock in the field will
                show as genuine to a consumer.
              </p>
            </div>`
          : null}
        ${soon.length > 0
          ? html`<div class="expiry-row">
              <b>${icon("hourglass_empty")} ${soon.length}
                ${soon.length === 1 ? "batch expires" : "batches expire"} within
                30 days</b>
              <p>${names(soon)}</p>
            </div>`
          : null}
      </div>
    `;
  }

  // Shelf life for a batch: the best-before plus how long is left. Days are
  // computed from UTC midnights so "expires today" reads as 0, matching what
  // the server sends to the tap page.
  private renderShelfLife(b: NfcBatch) {
    const days = this.daysToExpiry(b);
    if (days === null) {
      // Durable goods: show the production date alone, or nothing at all.
      const made = this.formatDate(b.ManufacturedAt);
      return made === "—" ? html`<span class="muted">—</span>` : html`${made}`;
    }
    return html`
      ${this.formatDate(b.ExpiresAt)}
      ${days < 0
        ? html`<span class="badge badge--bad"
            >${icon("flag")} Expired ${Math.abs(days)}d ago</span
          >`
        : days <= 30
          ? html`<span class="badge badge--neutral"
              >${icon("hourglass_empty")} ${days}d left</span
            >`
          : html`<small>${days}d left</small>`}
    `;
  }

  private openBatch(): void {
    this.batchProduct = this.products[0]?.ID ?? "";
    this.batchCount = "";
    this.batchManufactured = "";
    this.batchExpires = "";
    this.batchError = "";
    this.batchDialog?.show();
  }

  private openTag(): void {
    this.tagUID = "";
    this.tagProduct = this.products[0]?.ID ?? "";
    this.tagBatch = "";
    this.tagError = "";
    this.tagDialog?.show();
  }

  private async handleCreateBatch(e: Event): Promise<void> {
    e.preventDefault();
    this.batchError = "";
    const count = Number.parseInt(this.batchCount, 10);
    if (!this.batchProduct || !Number.isFinite(count) || count <= 0) {
      this.batchError = "Pick a product and a positive authorized count.";
      return;
    }
    if (
      this.batchManufactured &&
      this.batchExpires &&
      this.batchExpires < this.batchManufactured
    ) {
      this.batchError = "Best before cannot precede the manufacturing date.";
      return;
    }
    this.creatingBatch = true;
    try {
      const batch = await api.post<NfcBatch>("/nfc/batches", {
        productId: this.batchProduct,
        authorizedCount: count,
        manufacturedAt: this.batchManufactured || undefined,
        expiresAt: this.batchExpires || undefined,
      });
      this.batches = [batch, ...this.batches];
      this.batchDialog?.close();
      toast(`Created batch of ${batch.AuthorizedCount}`);
    } catch (err) {
      this.batchError = errorMessage(err, "Failed to create batch.");
    } finally {
      this.creatingBatch = false;
    }
  }

  private async handleProvision(e: Event): Promise<void> {
    e.preventDefault();
    this.tagError = "";
    const uid = this.tagUID.trim().toUpperCase();
    if (!/^[0-9A-F]+$/.test(uid)) {
      this.tagError = "UID must be hexadecimal (e.g. 04DE5F1EACC040).";
      return;
    }
    this.provisioning = true;
    try {
      const tag = await api.post<NfcTag>("/nfc/tags", {
        uid,
        productId: this.tagProduct,
        batchId: this.tagBatch || undefined,
      });
      this.tags = [tag, ...this.tags];
      this.tagDialog?.close();
      invalidateNavCounts();
      toast(`Provisioned tag ${tag.UID}`);
    } catch (err) {
      this.tagError = errorMessage(err, "Failed to provision tag.");
    } finally {
      this.provisioning = false;
    }
  }

  private renderBatches() {
    if (this.batches.length === 0) {
      return html`<div class="card card--flush empty">
        No batches yet. A batch authorizes how many tags may be provisioned for a
        product.
      </div>`;
    }
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>Batches <small>· ${this.batches.length}</small></h2>
        </div>
        <div class="table-scroll"><table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Reconciliation</th>
              <th>Shelf life</th>
              <th>Status</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            ${this.batches.map(
              (b) => html`
                <tr>
                  <td><b>${this.productName(b.ProductID)}</b></td>
                  <td>${this.renderProvisioning(b)}</td>
                  <td>${this.renderShelfLife(b)}</td>
                  <td>${this.renderBatchStatus(b)}</td>
                  <td>${this.formatDate(b.CreatedAt)}</td>
                </tr>
              `,
            )}
          </tbody>
        </table></div>
      </div>
    `;
  }

  // A batch is a licence to mint N genuine identities, so it is gated by DUAL
  // CONTROL: until someone other than its creator approves it, it provisions
  // nothing. This is where the second person clicks — the endpoint existed
  // from the start, but the portal never called it, so every approval to date
  // was a raw API call. The server enforces the "not the creator" rule and
  // answers self-approval with an error this surfaces verbatim.
  private renderBatchStatus(b: NfcBatch) {
    if (b.Status === "pending_approval" || b.Status === "legacy_unapproved") {
      return html`
        <span class="badge badge--bad">${icon("hourglass_empty")} ${b.Status}</span>
        ${this.canAuthor
          ? html`<md-text-button @click=${() => void this.approveBatch(b)}>
              Approve
            </md-text-button>`
          : null}
      `;
    }
    const open = b.Status === "open";
    return html`
      <span class="badge ${open ? "badge--ok" : "badge--neutral"}"
        >${icon(open ? "verified" : "schedule")} ${b.Status || "—"}</span
      >
    `;
  }

  private async approveBatch(b: NfcBatch): Promise<void> {
    try {
      await api.post<NfcBatch>(`/nfc/batches/${b.ID}/approve`, {});
      // Re-read the row through the SAME projection the list uses. The approve
      // response is a bare batch entity with no Provisioning breakdown, and
      // splicing that into the list silently degraded the reconciliation gauge
      // to a fabricated "all writes succeeded" bar — worst on the
      // legacy-unapproved batches this button exists for, which can already
      // carry failed writes and destroyed chips.
      const view = await api.get<NfcBatch>(`/nfc/batches/${b.ID}`);
      this.batches = this.batches.map((x) => (x.ID === view.ID ? view : x));
      toast(`Batch approved — provisioning is now open`);
    } catch (err) {
      // The likeliest refusal is dual control itself: the creator cannot be
      // the approver. Say exactly that rather than a generic failure.
      toast(errorMessage(err, "Could not approve the batch."), true);
    }
  }

  // renderProvisioning draws the batch's reconciliation breakdown (arch doc
  // §6.5): a segmented bar (written / failed / destroyed) over the authorized
  // count, plus a compact legend. Falls back to a flat count for older batches
  // the server returned without a Provisioning object.
  private renderProvisioning(b: NfcBatch) {
    const authorized = b.AuthorizedCount || 0;
    const p = b.Provisioning ?? {
      Authorized: authorized,
      Provisioned: this.provisionedCount(b.ID),
      WrittenOK: this.provisionedCount(b.ID),
      WriteFailed: 0,
      Destroyed: 0,
      Remaining: Math.max(0, authorized - this.provisionedCount(b.ID)),
    };
    const pct = (n: number) => (authorized ? (100 * n) / authorized : 0);
    const segs: [number, string][] = [
      [p.WrittenOK, "seg--ok"],
      [p.WriteFailed, "seg--failed"],
      [p.Destroyed, "seg--destroyed"],
    ];
    return html`
      <div class="progress">
        <div class="progress__track">
          ${segs.map(([n, cls]) =>
            n > 0
              ? html`<div
                  class="progress__seg ${cls}"
                  style="width: ${pct(n)}%"
                  title="${n}"
                ></div>`
              : null,
          )}
        </div>
        <span class="progress__label">${p.Provisioned} / ${authorized}</span>
      </div>
      <div class="recon">
        <span class="recon__dot recon__dot--ok"></span>${p.WrittenOK} written
        ${p.WriteFailed > 0
          ? html`<span class="recon__dot recon__dot--failed"></span>${p.WriteFailed}
              failed`
          : null}
        ${p.Destroyed > 0
          ? html`<span class="recon__dot recon__dot--destroyed"></span>${p.Destroyed}
              destroyed`
          : null}
        · ${p.Remaining} left
      </div>
    `;
  }

  private get fleet() {
    const active = this.tags.filter((t) => t.Status === "active").length;
    const reconciled = this.tags.filter(
      (t) => t.TerminalState === "written_ok" && t.CounterLastSeen > 0,
    ).length;
    const waiting = this.tags.filter((t) => this.isWaiting(t)).length;
    const anomalous = this.tags.filter((t) => this.isFlagged(t)).length;
    return { total: this.tags.length, active, reconciled, waiting, anomalous };
  }

  /** Clicking a metric focuses the tag list on it. */
  private focusList(filter: TagFilter): void {
    this.tagFilter = filter;
    this.renderRoot
      .querySelector(".tags-anchor")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  private renderStats() {
    const f = this.fleet;
    const scanned = f.total - f.waiting;
    const pct = f.total > 0 ? Math.round((scanned / f.total) * 100) : 0;
    // Clear, honest headline that reads the fleet's state at a glance.
    const headline =
      f.total === 0
        ? "No tags in the field yet"
        : f.anomalous > 0
          ? `${f.anomalous} ${f.anomalous === 1 ? "tag needs" : "tags need"} attention`
          : f.waiting > 0
            ? `${f.waiting} ${f.waiting === 1 ? "tag" : "tags"} awaiting first scan`
            : "Every tag verified in the field";
    const tone =
      f.total === 0 ? "idle" : f.anomalous > 0 ? "alert" : f.waiting > 0 ? "pending" : "ok";

    // A metric is a Material 3 tonal container; pressed = filled with its role.
    const metric = (
      key: TagFilter | null,
      value: number,
      label: string,
      variant: "" | "pending" | "alert",
    ) => html`
      <button
        class="metric ${variant ? `metric--${variant}` : ""}"
        ?disabled=${key === null || value === 0}
        aria-pressed=${key !== null && this.tagFilter === key ? "true" : "false"}
        @click=${() => key && this.focusList(key)}
      >
        <span class="metric__n"
          >${Math.round(value * this.countProgress).toLocaleString("en")}</span
        >
        <span class="metric__l">${label}</span>
      </button>
    `;

    return html`
      <section class="fleet fleet--${tone}">
        <div class="fleet__head">
          <span class="fleet__pulse" aria-hidden="true"></span>
          <div>
            <div class="fleet__title">${headline}</div>
            <div class="fleet__sub">
              ${f.total > 0
                ? html`${scanned.toLocaleString("en")} of
                    ${f.total.toLocaleString("en")} scanned at least once`
                : html`Provision a batch to put verified tags into circulation`}
            </div>
          </div>
          ${f.total > 0
            ? html`<div class="fleet__pct">
                <span class="fleet__pct-n"
                  >${Math.round(pct * this.countProgress)}%</span
                ><span>reached</span>
              </div>`
            : null}
        </div>
        ${f.total > 0
          ? html`<div
              class="fleet__bar"
              role="progressbar"
              aria-valuenow=${pct}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              <span class="fleet__fill" style="--pct:${pct}%"></span>
            </div>`
          : null}
        <div class="fleet__metrics">
          ${metric("all", f.active, "Active", "")}
          ${metric(null, f.reconciled, "Reconciled", "")}
          ${metric("wait", f.waiting, "Awaiting scan", "pending")}
          ${metric("flagged", f.anomalous, "Needs attention", "alert")}
        </div>
      </section>
    `;
  }

  private tagStatusBadge(t: NfcTag) {
    if (this.isFlagged(t)) {
      return html`<span class="badge badge--bad"
        >${icon("flag")} ${TERMINAL_LABELS[t.TerminalState] ??
        t.TerminalState}</span
      >`;
    }
    if (this.isWaiting(t)) {
      return html`<span class="badge badge--neutral"
        >${icon("hourglass_empty")} Awaiting first read</span
      >`;
    }
    return html`<span class="badge badge--ok"
      >${icon("verified")} ${t.Status === "active" ? "Verified" : t.Status}</span
    >`;
  }

  /** Open a freshly-computed genuine tap URL for a tag — what a customer
   * sees on tapping the chip. The server holds the SUN keys and returns the
   * next-counter URL; opening it advances the counter like a real tap. The
   * variant emulates a faithful NTAG 424 DNA config (plaintext / encrypted
   * PICCData / opened TagTamper seal). */
  private async previewTap(
    tag: NfcTag,
    variant: "plain" | "encrypted" | "tampered" = "plain",
  ): Promise<void> {
    // Open the tab synchronously (inside the click) so the popup blocker
    // allows it, then point it at the freshly-minted URL.
    const tab = window.open("about:blank", "_blank", "noopener");
    try {
      const preview = await api.get<{ path: string; counter: number }>(
        `/nfc/tags/${tag.ID}/preview-tap?variant=${variant}`,
      );
      if (tab) tab.location.href = preview.path;
      else window.open(preview.path, "_blank", "noopener");
    } catch (err) {
      tab?.close();
      toast(errorMessage(err, "Could not build a preview tap."));
    }
  }

  private togglePreviewMenu(tagID: string): void {
    this.openPreviewMenu = this.openPreviewMenu === tagID ? "" : tagID;
  }

  private renderTags() {
    if (this.tags.length === 0) {
      return html`<div class="card card--flush empty">
        No tags provisioned yet.${this.canAuthor
          ? " Provision your first tag to bind a chip to a product."
          : ""}
      </div>`;
    }
    return html`
      <div class="card card--flush tags-anchor">
        <div class="cardhead">
          <h2>Recent tag activity</h2>
          <md-chip-set>
            ${(
              [
                ["all", "All"],
                ["flagged", "Flagged"],
                ["wait", "Never read"],
              ] as [TagFilter, string][]
            ).map(
              ([key, label]) => html`
                <md-filter-chip
                  label=${label}
                  ?selected=${this.tagFilter === key}
                  @click=${(e: Event) => {
                    e.preventDefault();
                    this.tagFilter = key;
                  }}
                ></md-filter-chip>
              `,
            )}
          </md-chip-set>
        </div>
        <div class="table-scroll"><table>
          <thead>
            <tr>
              <th>Tag UID</th>
              <th>Product</th>
              <th>Reads</th>
              <th>Batch</th>
              <th>Status</th>
              <th>Provisioned</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${this.filteredTags.length === 0
              ? html`<tr>
                  <td colspan="7" class="empty">No tags in this view</td>
                </tr>`
              : this.filteredTags.map(
                  (t) => html`
                    <tr>
                      <td><code>${t.UID}</code></td>
                      <td>${this.productName(t.ProductID)}</td>
                      <td>${t.CounterLastSeen}</td>
                      <td>
                        ${t.BatchID
                          ? html`<code>${t.BatchID.slice(0, 8)}</code>`
                          : html`<small>—</small>`}
                      </td>
                      <td>${this.tagStatusBadge(t)}</td>
                      <td>${this.formatDate(t.CreatedAt)}</td>
                      <td class="tag-actions">
                        ${t.Status === "active"
                          ? html`<div class="preview-split">
                              <button
                                class="preview-btn"
                                @click=${() => void this.previewTap(t, "plain")}
                                title="Open the consumer tap page for this tag"
                              >
                                ${icon("contactless")}<span>Preview tap</span>
                              </button>
                              <button
                                class="preview-caret"
                                id="pv-${t.ID}"
                                aria-label="More preview options"
                                aria-haspopup="menu"
                                @click=${() => this.togglePreviewMenu(t.ID)}
                              >
                                ${icon("expand_more")}
                              </button>
                              <md-menu
                                anchor="pv-${t.ID}"
                                ?open=${this.openPreviewMenu === t.ID}
                                @closed=${() => (this.openPreviewMenu = "")}
                                positioning="popover"
                                menu-corner="start-end"
                                anchor-corner="end-end"
                              >
                                <md-menu-item
                                  @click=${() => void this.previewTap(t, "plain")}
                                >
                                  <span slot="headline">Genuine</span>
                                  <span slot="supporting-text"
                                    >Standard tap · plaintext mirror</span
                                  >
                                </md-menu-item>
                                <md-menu-item
                                  @click=${() =>
                                    void this.previewTap(t, "encrypted")}
                                >
                                  <span slot="headline">Genuine · private</span>
                                  <span slot="supporting-text"
                                    >Encrypted mirror · UID hidden in the URL</span
                                  >
                                </md-menu-item>
                                <md-menu-item
                                  @click=${() =>
                                    void this.previewTap(t, "tampered")}
                                >
                                  <span slot="headline">Seal opened</span>
                                  <span slot="supporting-text"
                                    >TagTamper · authenticated broken seal</span
                                  >
                                </md-menu-item>
                              </md-menu>
                            </div>`
                          : null}
                      </td>
                    </tr>
                  `,
                )}
          </tbody>
        </table></div>
      </div>
    `;
  }

  /** Consumer reports filed from the public tap page ("this looks
   * suspicious"). Highest-signal rows are reports on unknown or replayed
   * taps — probable counterfeits in the wild. */
  private renderReports() {
    if (this.reports.length === 0) return null;
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>
            Consumer reports
            <small>· ${this.reports.length} from the tap page</small>
          </h2>
        </div>
        <div class="table-scroll"><table>
          <thead>
            <tr>
              <th>Reported</th>
              <th>Product</th>
              <th>Tap showed</th>
              <th>Note</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${this.reports.map(
              (r) => html`
                <tr>
                  <td>${new Date(r.createdAt).toLocaleString()}</td>
                  <td>
                    ${r.productId
                      ? this.productName(r.productId)
                      : html`<span class="badge badge--bad">
                          ${icon("gpp_maybe")} unknown tag
                        </span>`}
                    <small class="mono">${r.uid}</small>
                  </td>
                  <td>
                    <span
                      class="badge ${r.verdict === "verified"
                        ? "badge--ok"
                        : "badge--bad"}"
                      >${r.verdict}</span
                    >
                  </td>
                  <td class="report-note">
                    ${r.note || html`<span class="muted">—</span>`}
                  </td>
                  <td>
                    <span
                      class="badge ${r.status === "new"
                        ? "badge--neutral"
                        : "badge--ok"}"
                      >${r.status}</span
                    >
                  </td>
                </tr>
              `,
            )}
          </tbody>
        </table></div>
      </div>
    `;
  }

  render() {
    return html`
      <app-shell active="/nfc">
        <div class="page">
          <div class="page-header">
            <div>
              <h1>NFC tags</h1>
              <p>
                Provision NTAG 424 DNA tags and track their batches. A consumer
                tap verifies each tag's SUN signature against this registry.
              </p>
            </div>
            ${this.canAuthor
              ? html`
                  <div class="header-actions">
                    <md-text-button @click=${() => this.openBatch()}>
                      <span slot="icon">${icon("add")}</span>
                      New batch
                    </md-text-button>
                    <md-filled-button
                      @click=${() => this.openTag()}
                      ?disabled=${this.products.length === 0}
                    >
                      <span slot="icon">${icon("contactless")}</span>
                      Provision tag
                    </md-filled-button>
                  </div>
                `
              : null}
          </div>

          ${this.loading
            ? html`<div class="center-loading">
                <md-circular-progress indeterminate></md-circular-progress>
              </div>`
            : this.error
              ? html`<p class="error-text">${this.error}</p>`
              : this.products.length === 0
                ? html`<div class="card card--flush empty">
                    Create a product first — tags and batches bind to a product.
                    <a href="/products">Go to products →</a>
                  </div>`
                : html`
                    ${this.renderExpiryWarning()} ${this.renderStats()}
                    ${this.renderReports()} ${this.renderBatches()}
                    ${this.renderTags()}
                  `}
        </div>

        <md-dialog id="batch-dialog">
          <div slot="headline">New batch</div>
          <form
            slot="content"
            id="batch-form"
            class="dialog-form"
            @submit=${(e: Event) => void this.handleCreateBatch(e)}
          >
            <md-outlined-select
              label="Product"
              required
              .value=${this.batchProduct}
              @change=${(e: Event) => (this.batchProduct = inputValue(e))}
            >
              ${this.products.map(
                (p) => html`
                  <md-select-option value=${p.ID}>
                    <div slot="headline">${p.Name}</div>
                  </md-select-option>
                `,
              )}
            </md-outlined-select>
            <md-outlined-text-field
              label="Authorized count"
              type="number"
              inputmode="numeric"
              min="1"
              required
              .value=${this.batchCount}
              @input=${(e: Event) => (this.batchCount = inputValue(e))}
            ></md-outlined-text-field>
            <md-outlined-text-field
              label="Manufactured on (optional)"
              type="date"
              .value=${this.batchManufactured}
              @input=${(e: Event) => (this.batchManufactured = inputValue(e))}
            ></md-outlined-text-field>
            <md-outlined-text-field
              label="Best before (optional)"
              type="date"
              supporting-text="Leave empty for goods that do not expire."
              .value=${this.batchExpires}
              @input=${(e: Event) => (this.batchExpires = inputValue(e))}
            ></md-outlined-text-field>
            ${this.batchError
              ? html`<p class="error-text">${this.batchError}</p>`
              : null}
          </form>
          <div slot="actions">
            <md-text-button @click=${() => this.batchDialog?.close()}>
              Cancel
            </md-text-button>
            <md-filled-button
              form="batch-form"
              type="submit"
              ?disabled=${this.creatingBatch}
            >
              ${this.creatingBatch ? "Creating…" : "Create batch"}
            </md-filled-button>
          </div>
        </md-dialog>

        <md-dialog id="tag-dialog">
          <div slot="headline">Provision tag</div>
          <form
            slot="content"
            id="tag-form"
            class="dialog-form"
            @submit=${(e: Event) => void this.handleProvision(e)}
          >
            <md-outlined-text-field
              label="Chip UID (hex)"
              required
              placeholder="04DE5F1EACC040"
              .value=${this.tagUID}
              @input=${(e: Event) => (this.tagUID = inputValue(e))}
            ></md-outlined-text-field>
            <md-outlined-select
              label="Product"
              required
              .value=${this.tagProduct}
              @change=${(e: Event) => (this.tagProduct = inputValue(e))}
            >
              ${this.products.map(
                (p) => html`
                  <md-select-option value=${p.ID}>
                    <div slot="headline">${p.Name}</div>
                  </md-select-option>
                `,
              )}
            </md-outlined-select>
            <md-outlined-select
              label="Batch (optional)"
              .value=${this.tagBatch}
              @change=${(e: Event) => (this.tagBatch = inputValue(e))}
            >
              <md-select-option value="">
                <div slot="headline">— None —</div>
              </md-select-option>
              ${this.batches.map(
                (b) => html`
                  <md-select-option value=${b.ID}>
                    <div slot="headline">
                      ${this.productName(b.ProductID)} ·
                      ${this.provisionedCount(b.ID)}/${b.AuthorizedCount}
                    </div>
                  </md-select-option>
                `,
              )}
            </md-outlined-select>
            ${this.tagError
              ? html`<p class="error-text">${this.tagError}</p>`
              : null}
          </form>
          <div slot="actions">
            <md-text-button @click=${() => this.tagDialog?.close()}>
              Cancel
            </md-text-button>
            <md-filled-button
              form="tag-form"
              type="submit"
              ?disabled=${this.provisioning}
            >
              ${this.provisioning ? "Provisioning…" : "Provision"}
            </md-filled-button>
          </div>
        </md-dialog>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "nfc-page": NfcPage;
  }
}
